import { CaregiverId } from "../../../../../shared/schema/schema";
import type { PatientCalendarSelection } from "./patientCalendarSelection";
import SingleDatePicker from "../../../../../shared/components/DatePicker/SingleDatePicker";
import useCalendarSelection from "../../../../entityProfileModal/sections/calendar/useCalendarSelection";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Checkbox,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Tooltip,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import type { Messages, ResponseOf } from "../../../../../core/api";
import { auth } from "../../../../../core/auth";
import useAgencyMembers from "../../../../../shared/hooks/useAgencyMembers";
import useApi from "../../../../../shared/hooks/useApi";
import useLocalStorage from "../../../../../shared/hooks/useLocalStorage";
import type {
  PatientId,
  PatientTaskInstanceId,
  PatientVacationId,
  VisitBroadcastId,
  VisitInstanceId,
} from "../../../../../shared/schema/schema";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import EntityProfileDataTable, {
  type EntityProfileDataTableColumn,
} from "../../../../entityProfileModal/components/EntityProfileDataTable";
import EntityProfileCalendar from "../../../../entityProfileModal/sections/calendar/EntityProfileCalendar";
import {
  formatEntityCalendarDate,
  getEntityCalendarBroadcastStatusLabel,
  getEntityCalendarVisibleMonthDates,
  getPatientCalendarVisitBroadcastKind,
  toEntityCalendarLocalDate,
  type EntityCalendarBroadcastStatus,
  type EntityProfileCalendarMarker,
  type EntityProfileCalendarVacation,
  type EntityProfileCalendarVisit,
} from "../../../../entityProfileModal/sections/calendar/entityProfileCalendar.utils";
import type { PatientProfileBridgeActions } from "../../../patientProfileModal.actions";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import PatientTaskMetricsModal from "../../../../patientTasksMetrics/PatientTaskMetricsModal";
import {
  getPatientCalendarQueryKeyPrefix,
  usePatientProfileCalendarRange,
} from "./PatientProfileCalendarSection.hooks";
import type {
  PatientCalendarFlexibleBroadcastLike,
  PatientCalendarNoteLike,
  PatientCalendarTaskLike,
  PatientCalendarVacationLike,
  PatientCalendarVisitLike,
} from "./patientCalendar.utils";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";
import IosInformationCircleIcon from "../../../../../shared/icons/IosInformationCircleIcon";

type CalendarView = "calendar" | "list";

type Props = {
  actions: PatientProfileBridgeActions;
  /**
   * The card is kept mounted but hidden (the "save calendar selection" setting
   * while another section is open). It then loads nothing until shown again.
   */
  isHidden?: boolean;
  patient?: PatientProfilePatient;
  patientId: PatientId;
  showBillingFeature: boolean;
  view: CalendarView;
};

const patientCalendarStorageKeys = {
  showAuthorizationDetails: "patientCalendarShowAuthorizationDetails",
  showDeletedVisits: "patientCalendarShowDeletedVisits",
  showSummaryColumn: "patientCalendarShowSummaryColumn",
};
const entityProfileCalendarReadyEvent = "medflyt:entity-profile-calendar-ready";

function isDateInRange(date: LocalDate, from: LocalDate, to: LocalDate) {
  return date.isBefore(from) === false && date.isAfter(to) === false;
}

/**
 * `Array.prototype.filter` always allocates a new array, so using it inside a
 * state updater schedules a re-render even when nothing was removed. Returning
 * the original array lets React bail out instead.
 */
function filterPreservingIdentity<T>(items: T[], predicate: (item: T) => boolean): T[] {
  const next = items.filter(predicate);

  return next.length === items.length ? items : next;
}

type PatientAuthorizationRow = ResponseOf<
  "get",
  "./authorizations/:patientId"
>["authorizations"][number];
type AuthorizationSortKey = "startDate" | "endDate";

const authorizationPageSizeOptions = [3, 5, 10, 25, 50, 100];

const authorizationPeriodTypeLabels: Record<string, string> = {
  DAILY: "Daily",
  ENTIRE_PERIOD: "Entire period",
  MONTHLY: "Monthly",
  WEEKLY: "Weekly",
};
const authorizationDayMinuteFields: Array<{
  key:
    | "mondayMinutes"
    | "tuesdayMinutes"
    | "wednesdayMinutes"
    | "thursdayMinutes"
    | "fridayMinutes"
    | "saturdayMinutes"
    | "sundayMinutes";
  label: string;
}> = [
  { key: "mondayMinutes", label: "Mo" },
  { key: "tuesdayMinutes", label: "Tu" },
  { key: "wednesdayMinutes", label: "We" },
  { key: "thursdayMinutes", label: "Th" },
  { key: "fridayMinutes", label: "Fr" },
  { key: "saturdayMinutes", label: "Sa" },
  { key: "sundayMinutes", label: "Su" },
];

function getVisitStatus(visit: PatientCalendarVisitLike): EntityProfileCalendarVisit["status"] {
  if (visit.visit.removedAt) {
    return "deleted";
  }

  if (visit.visit.missedVisit) {
    return "missed";
  }

  if (visit.taskDetails?.taskName) {
    return "task";
  }

  return "visit";
}

function getVisitTitle(visit: PatientCalendarVisitLike) {
  const visitWithDisplayData = visit.visit;

  // The only name the calendar endpoint actually returns - it lives on the
  // nested `visit` (VisitInstanceCalendarExtended), not on the row. The
  // fallbacks below are optional augmentations that the payload never has.
  if (visitWithDisplayData.caregiverDisplayName) {
    return visitWithDisplayData.caregiverDisplayName;
  }

  if (visitWithDisplayData.caregiver?.displayName) {
    return visitWithDisplayData.caregiver.displayName;
  }

  if (visitWithDisplayData.caregiver?.fullName) {
    return visitWithDisplayData.caregiver.fullName;
  }

  if (visit.caregiverDisplayName) {
    return visit.caregiverDisplayName;
  }

  if (visitWithDisplayData.caregiverName) {
    return visitWithDisplayData.caregiverName;
  }

  if (visit.visit.caregiverId === null || visit.visit.caregiverId === undefined) {
    return "Unassigned";
  }

  return `Caregiver #${visit.visit.caregiverId}`;
}

function getVisitAuthorizationStatuses(visit: PatientCalendarVisitLike) {
  const statuses: EntityProfileCalendarVisit["authorizationStatuses"] = [];

  if (
    visit.missingAuth ||
    visit.issue_invalid_authorization_assignment ||
    visit.issue_no_matching_authorization
  ) {
    statuses.push({ message: "No Authorization" });
  }

  if (visit.issue_missing_authorization_hours) {
    statuses.push({
      message: "Auth Insufficient Hours",
      tooltip: visit.issue_missing_authorization_hours,
    });
  }

  if (visit.issue_authorization_over_allocation) {
    statuses.push({
      message: "Auth Over Allocation",
      tooltip: visit.issue_authorization_over_allocation,
    });
  }

  if (visit.issue_schedule_auth_allocation_conflict) {
    statuses.push({
      message: "Auth Conflict",
      tooltip: visit.issue_schedule_auth_allocation_conflict,
    });
  }

  if (visit.issue_excessive_clock_distance) {
    statuses.push({
      backgroundColor: "#f39c12",
      message: "GPS Location Mismatch",
      tooltip: visit.issue_excessive_clock_distance,
    });
  }

  return statuses.length > 0 ? statuses : null;
}

function normalizeVisit(visit: PatientCalendarVisitLike): EntityProfileCalendarVisit {
  const normalized: EntityProfileCalendarVisit = {
    addressTimezone: visit.visit.address?.timezone,
    authorizationStatuses: getVisitAuthorizationStatuses(visit),
    billedSeconds: visit.billedSeconds,
    caregiverId: visit.visit.caregiverId,
    certification: visit.certification,
    clockinTime: visit.visit.clockinTime,
    clockoutTime: visit.visit.clockoutTime,
    date: visit.visitDate,
    endTime: visit.visit.endTime,
    hasGpsIssue: Boolean(visit.issue_excessive_clock_distance),
    id: visit.visit.id,
    isBilled: (visit.billedSeconds ?? 0) > 0,
    isOnPayrollDraft: (visit.draftPaidSeconds ?? 0) > 0,
    isOnWeeklyTemplate: visit.isOnWeeklyTemplate,
    isPaid: visit.isPaid,
    missedDuties: visit.missedDuties,
    missedVisitReason: visit.visit.missedVisitReason,
    paidSeconds: visit.paidSeconds ?? visit.draftPaidSeconds,
    payrollDraftSeconds: visit.draftPaidSeconds,
    parentSplitVisitInstanceId: visit.parentSplitVisitInstanceId,
    childSplitVisitInstanceId: visit.childSplitVisitInstanceId,
    pendingConfirmationUntil: visit.pendingConfirmationUntil,
    startTime: visit.visit.startTime,
    status: getVisitStatus(visit),
    subtitle: visit.taskDetails?.taskName ?? undefined,
    title: getVisitTitle(visit),
    visitBatchId: visit.visit.visitBatchId,
    visitBatchMaxEndDate: visit.visitBatchMaxEndDate,
    visitBatchMinStartDate: visit.visitBatchMinStartDate,
    visitBatchType: visit.visitBatchType,
    visitBroadcast: visit.visitBroadcast,
  };

  return { ...normalized, broadcastKind: getPatientCalendarVisitBroadcastKind(normalized) };
}

function normalizeVacation(vacation: PatientCalendarVacationLike) {
  return {
    date: vacation.date,
    id: vacation.id,
  };
}

function normalizeNote(note: PatientCalendarNoteLike) {
  return {
    date: note.calendarDate,
    id: note.id,
  };
}

function getTaskState(task: PatientCalendarTaskLike) {
  const taskType = "type" in task ? task.type : undefined;

  return task.state ?? task.status ?? taskType ?? "Unstaffed";
}

function getTaskDetailLabel(task: PatientCalendarTaskLike) {
  const taskState = getTaskState(task);

  if (taskState === "Future") {
    return task.isFutureAssigned === true ? "Future (Assigned)" : "Future (Unstaffed)";
  }

  if (taskState === "Assigned" && task.scheduleDateTime === null) {
    return "Assigned (Specific date not selected)";
  }

  return taskState === "FutureTask" ? "Future (Unstaffed)" : taskState;
}

function normalizeTask(
  task: PatientCalendarTaskLike,
  actions: {
    onCancelTask: (task: PatientCalendarTaskLike) => void;
    onEditTask: (task: PatientCalendarTaskLike) => void;
    onOpenTaskMetrics: (task: PatientCalendarTaskLike) => void;
    onScheduleTask: (task: PatientCalendarTaskLike) => void;
    onStopTaskBroadcast: (task: PatientCalendarTaskLike) => void;
  }
): EntityProfileCalendarMarker | null {
  const date = task.startDate ?? task.dueDate;

  if (date === undefined) {
    return null;
  }

  const taskState = getTaskState(task);
  const markerActions =
    taskState === "Broadcasting"
      ? [
          { label: "Stop broadcast", onClick: () => actions.onStopTaskBroadcast(task) },
          { label: "View metrics", onClick: () => actions.onOpenTaskMetrics(task) },
        ]
      : [
          { label: "Edit", onClick: () => actions.onEditTask(task) },
          { label: "Cancel", onClick: () => actions.onCancelTask(task) },
          ...(taskState === "Assigned" && task.scheduleDateTime === null
            ? [{ label: "Schedule", onClick: () => actions.onScheduleTask(task) }]
            : []),
        ];

  return {
    colorScheme: taskState === "Broadcasting" ? "orange" : "blue",
    date,
    detailLabel: getTaskDetailLabel(task),
    id: `task-${task.taskInstanceId ?? task.taskId ?? `${task.title}-${date}`}`,
    label: task.title ?? "Task",
    actions: markerActions,
  };
}

function normalizeFlexibleBroadcast(
  broadcast: PatientCalendarFlexibleBroadcastLike,
  actions: PatientProfileBridgeActions,
  onStopBroadcast: (broadcastId: VisitBroadcastId) => void
): EntityProfileCalendarMarker[] {
  if (broadcast.cancelled === true) {
    return [];
  }

  // calendar-flexible-visit-broadcast-indicator-bar.html keys off
  // `pendingConfirmationUntil` being set at all - it does not check that it is still
  // in the future - and only then falls back to the requested-amount split.
  const statusKind: EntityCalendarBroadcastStatus =
    broadcast.pendingConfirmationUntil !== null && broadcast.pendingConfirmationUntil !== undefined
      ? "PENDING_CONFIRMATION"
      : broadcast.requestedAmount === undefined ||
        broadcast.requestedAmount === null ||
        broadcast.requestedAmount === 0
      ? "PENDING_REQUESTS"
      : "AWAITING_ASSIGNMENT";

  return [
    {
      actions: [
        {
          label: "Info",
          onClick: () => actions.navigateToVisitBroadcast(broadcast.id),
        },
        {
          label: "Stop broadcast",
          onClick: () => onStopBroadcast(broadcast.id),
        },
      ],
      colorScheme: "purple",
      date: broadcast.startDate,
      detailLabel: "Broadcasted",
      endDate: broadcast.endDate,
      id: `broadcast-${broadcast.id}`,
      label: "Flexible visit broadcast",
      pendingConfirmationUntil: broadcast.pendingConfirmationUntil,
      statusCount: broadcast.requestedAmount,
      statusKind: statusKind,
      statusLabel: getEntityCalendarBroadcastStatusLabel(statusKind),
    },
  ];
}

function PatientCalendarActionButtons(props: {
  actions: PatientProfileBridgeActions;
  patient: PatientProfilePatient;
  selectedVacations: EntityProfileCalendarVacation[];
  selectedVisits: EntityProfileCalendarVisit[];
  onDeleteVacations: () => void;
  onStopBroadcasts: (visits: EntityProfileCalendarVisit[]) => void;
  onActionDone: () => void;
  isStoppingBroadcasts: boolean;
  calendarSelection?: PatientCalendarSelection;
  isSelectingVisits?: boolean;
}) {
  const hasSelectedVacations = props.selectedVacations.length > 0;
  const hasSelectedVisits = props.selectedVisits.length > 0 && !props.isSelectingVisits;
  const selectedBroadcasts = props.selectedVisits.filter(isBroadcastListVisit);
  const hasOnlyVisits = hasSelectedVisits && hasSelectedVacations === false;
  const hasOnlyVacations = hasSelectedVacations && hasSelectedVisits === false;
  const hasOnlyBroadcasts =
    selectedBroadcasts.length > 0 &&
    selectedBroadcasts.length === props.selectedVisits.length &&
    hasSelectedVacations === false;
  const hasOnlyVisitItemsSelected = hasOnlyVisits;
  const allSelectedHaveGpsIssue =
    props.selectedVisits.length > 0 && props.selectedVisits.every((visit) => visit.hasGpsIssue);
  const isApproveClockLocationEnabled = hasOnlyVisitItemsSelected && allSelectedHaveGpsIssue;
  const canUseBulkActions = auth.isAllowedTo(
    "edit_visit_instance_general_info" as Messages["AgencyMemberPermission"]
  );
  const canCreateVisit = auth.isAllowedTo(
    "create_new_patient_visit" as Messages["AgencyMemberPermission"]
  );
  const canEditVisit = auth.isAllowedTo("edit_patient_visit" as Messages["AgencyMemberPermission"]);
  const canDeleteVisit = auth.isAllowedTo(
    "delete_patient_visit" as Messages["AgencyMemberPermission"]
  );

  return (
    <HStack flexShrink={0} flexWrap="nowrap" justifyContent="flex-end" spacing={2}>
      {canUseBulkActions && canCreateVisit ? (
        <EntityModalButton
          kind="blue"
          size="sm"
          onClick={() => props.actions.openNewVisit({ patient: props.patient })}
        >
          New Visit
        </EntityModalButton>
      ) : null}
      {canUseBulkActions && canEditVisit ? (
        <EntityModalButton
          disabled={!isApproveClockLocationEnabled}
          kind="blue"
          size="sm"
          onClick={() =>
            props.actions.approveCalendarClockLocation({
              patient: props.patient,
              selectedVisits: props.selectedVisits,
              calendarSelection: props.calendarSelection,
              onDone: props.onActionDone,
            })
          }
        >
          Approve Clock Location
        </EntityModalButton>
      ) : null}
      {canUseBulkActions && canEditVisit ? (
        <EntityModalButton
          disabled={hasOnlyVisits === false}
          kind="blue"
          size="sm"
          onClick={() =>
            props.actions.editCalendarItems({
              patient: props.patient,
              selectedVisits: props.selectedVisits,
              calendarSelection: props.calendarSelection,
              targetElementId: "scroll-calendar",
              onDone: props.onActionDone,
            })
          }
        >
          Edit
        </EntityModalButton>
      ) : null}
      {canUseBulkActions && canEditVisit ? (
        <EntityModalButton
          disabled={!hasOnlyBroadcasts || props.isStoppingBroadcasts}
          isLoading={props.isStoppingBroadcasts}
          kind="red"
          size="sm"
          onClick={() => props.onStopBroadcasts(selectedBroadcasts)}
        >
          Stop Broadcast
        </EntityModalButton>
      ) : null}
      {canUseBulkActions && canEditVisit ? (
        <EntityModalButton
          disabled={!hasOnlyVisits}
          kind="red"
          size="sm"
          onClick={() =>
            props.actions.missCalendarItems({
              patient: props.patient,
              selectedVisits: props.selectedVisits,
              calendarSelection: props.calendarSelection,
              onDone: props.onActionDone,
            })
          }
        >
          Miss
        </EntityModalButton>
      ) : null}
      {canUseBulkActions && canDeleteVisit && props.selectedVacations.length > 0 ? (
        <EntityModalButton
          disabled={!hasOnlyVacations}
          kind="red"
          size="sm"
          onClick={props.onDeleteVacations}
        >
          Delete Vacation
        </EntityModalButton>
      ) : canUseBulkActions && canDeleteVisit ? (
        <EntityModalButton
          disabled={!hasOnlyVisits}
          kind="red"
          size="sm"
          onClick={() =>
            props.actions.deleteCalendarItems({
              patient: props.patient,
              selectedVisits: props.selectedVisits,
              calendarSelection: props.calendarSelection,
              onDone: props.onActionDone,
            })
          }
        >
          Delete
        </EntityModalButton>
      ) : null}
    </HStack>
  );
}

function isBroadcastListVisit(visit: EntityProfileCalendarVisit) {
  return (
    visit.visitBroadcast?.visitBroadcastId !== null &&
    visit.visitBroadcast?.visitBroadcastId !== undefined
  );
}

function PatientCalendarListActionButtons(props: {
  actions: PatientProfileBridgeActions;
  patient: PatientProfilePatient;
  selectedVacations: EntityProfileCalendarVacation[];
  selectedVisits: EntityProfileCalendarVisit[];
  onDeleteVacations: () => void;
  onStopBroadcasts: (visits: EntityProfileCalendarVisit[]) => void;
  onActionDone: () => void;
  isStoppingBroadcasts: boolean;
  calendarSelection?: PatientCalendarSelection;
  isSelectingVisits?: boolean;
}) {
  const selectedBroadcasts = props.selectedVisits.filter(isBroadcastListVisit);
  const selectedVisitsNonBroadcasts = props.selectedVisits.filter(
    (visit) => isBroadcastListVisit(visit) === false
  );
  const hasSelectedVacations = props.selectedVacations.length > 0;
  const isEditEnabled =
    hasSelectedVacations === false &&
    !props.isSelectingVisits &&
    ((selectedVisitsNonBroadcasts.length > 0 && selectedBroadcasts.length === 0) ||
      (selectedBroadcasts.length > 0 && selectedVisitsNonBroadcasts.length === 0));
  const isDeleteEnabled =
    selectedBroadcasts.length === 0 &&
    !props.isSelectingVisits &&
    ((selectedVisitsNonBroadcasts.length > 0 && hasSelectedVacations === false) ||
      (hasSelectedVacations && selectedVisitsNonBroadcasts.length === 0));
  const isStopBroadcastEnabled =
    selectedBroadcasts.length > 0 &&
    selectedVisitsNonBroadcasts.length === 0 &&
    hasSelectedVacations === false;
  const canUseBulkActions = auth.isAllowedTo(
    "edit_visit_instance_general_info" as Messages["AgencyMemberPermission"]
  );
  const canEditVisit = auth.isAllowedTo("edit_patient_visit" as Messages["AgencyMemberPermission"]);
  const canDeleteVisit = auth.isAllowedTo(
    "delete_patient_visit" as Messages["AgencyMemberPermission"]
  );

  return (
    <HStack flexShrink={0} flexWrap="nowrap" justifyContent="flex-end" spacing={2}>
      {canUseBulkActions && canEditVisit ? (
        <EntityModalButton
          disabled={!isEditEnabled}
          kind="blue"
          size="sm"
          onClick={() =>
            props.actions.editCalendarItems({
              patient: props.patient,
              selectedVisits: props.selectedVisits,
              calendarSelection: props.calendarSelection,
              targetElementId: "scroll-list-view",
              onDone: props.onActionDone,
            })
          }
        >
          Edit
        </EntityModalButton>
      ) : null}
      {canUseBulkActions && canDeleteVisit ? (
        <EntityModalButton
          disabled={!isDeleteEnabled}
          kind="red"
          size="sm"
          onClick={() => {
            if (hasSelectedVacations) {
              props.onDeleteVacations();
            } else {
              props.actions.deleteCalendarItems({
                patient: props.patient,
                selectedVisits: props.selectedVisits,
                calendarSelection: props.calendarSelection,
                onDone: props.onActionDone,
              });
            }
          }}
        >
          Delete
        </EntityModalButton>
      ) : null}
      {canUseBulkActions && canEditVisit ? (
        <EntityModalButton
          disabled={!isStopBroadcastEnabled || props.isStoppingBroadcasts}
          isLoading={props.isStoppingBroadcasts}
          kind="red"
          size="sm"
          onClick={() => props.onStopBroadcasts(selectedBroadcasts)}
        >
          Stop Broadcast
        </EntityModalButton>
      ) : null}
    </HStack>
  );
}

function formatAuthorizationDate(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (value instanceof LocalDate) {
    return dateFormatter.toDate(value);
  }

  try {
    return dateFormatter.toDate(LocalDate.parse(String(value).slice(0, 10)));
  } catch {
    return String(value);
  }
}

function formatAuthorizationHours(value: unknown) {
  if (typeof value !== "number") {
    return "";
  }

  return value % 60 === 0 ? String(value / 60) : (value / 60).toFixed(2);
}

function getAuthorizationPeriodSubLabel(row: PatientAuthorizationRow) {
  if (row.periodType !== "WEEKLY") {
    return "";
  }

  switch (row.firstDayOfWeek) {
    case "SUNDAY":
      return "(Sun-Sat)";
    case "MONDAY":
      return "(Mon-Sun)";
    case "TUESDAY":
      return "(Tue-Mon)";
    case "WEDNESDAY":
      return "(Wed-Tue)";
    case "THURSDAY":
      return "(Thu-Wed)";
    case "FRIDAY":
      return "(Fri-Thu)";
    case "SATURDAY":
      return "(Sat-Fri)";
    default:
      return "";
  }
}

function getAuthorizationContractName(
  contracts: PatientProfilePatient["contracts"],
  row: PatientAuthorizationRow
) {
  const contract = contracts.find((item) => item.id === row.patientContractId);

  return contract?.contractTypeName ?? "";
}

function formatAuthorizationServiceCode(value: unknown) {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "object") {
    const serviceCode = value as { code?: unknown; name?: unknown };

    return String(serviceCode.code ?? serviceCode.name ?? "");
  }

  return String(value);
}

function formatAgencyMemberName(member: { firstName?: string | null; lastName?: string | null }) {
  return [member.firstName, member.lastName].filter(Boolean).join(" ");
}

function getAuthorizationAgencyMemberName(
  row: PatientAuthorizationRow,
  nameField: "createdByName" | "updatedByName",
  idField: "createdBy" | "updatedBy",
  agencyMemberNamesById: Map<number, string>
) {
  if (typeof row[nameField] === "string" && row[nameField].trim() !== "") {
    return row[nameField];
  }

  if (typeof row[idField] === "number") {
    return agencyMemberNamesById.get(row[idField]) ?? "";
  }

  return "";
}

const authorizationDayTooltips: Record<typeof authorizationDayMinuteFields[number]["key"], string> =
  {
    fridayMinutes: "Friday",
    mondayMinutes: "Monday",
    saturdayMinutes: "Saturday",
    sundayMinutes: "Sunday",
    thursdayMinutes: "Thursday",
    tuesdayMinutes: "Tuesday",
    wednesdayMinutes: "Wednesday",
  };

function AuthorizationPeriodValue(props: { authorization: PatientAuthorizationRow }) {
  const authorization = props.authorization;
  const periodType =
    authorization.periodTypeDisplayName ??
    authorizationPeriodTypeLabels[authorization.periodType] ??
    authorization.periodType;

  if (authorization.periodType === "DAILY") {
    return (
      <VStack alignItems="center" spacing={0}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(7, 30px)"
          justifyContent="center"
          lineHeight="1.1"
        >
          {authorizationDayMinuteFields.map((field) => (
            <Tooltip key={field.key} label={authorizationDayTooltips[field.key]}>
              <Text textAlign="center">{formatAuthorizationHours(authorization[field.key])}</Text>
            </Tooltip>
          ))}
        </Box>
        <Text as="small">{periodType}</Text>
      </VStack>
    );
  }

  return (
    <>
      {formatAuthorizationHours(authorization.minutes)}
      <br />
      <Text as="small">{periodType}</Text>
      {getAuthorizationPeriodSubLabel(authorization) ? (
        <>
          <br />
          <Text as="small">{getAuthorizationPeriodSubLabel(authorization)}</Text>
        </>
      ) : null}
    </>
  );
}

function AuthorizationAuditValue(props: { date: unknown; name: string }) {
  return (
    <VStack alignItems="center" spacing={0}>
      <Text whiteSpace="nowrap">{formatAuthorizationDate(props.date)}</Text>
      <Text>{props.name}</Text>
    </VStack>
  );
}

export function PatientAuthorizationDetailsTable(props: {
  actions: PatientProfileBridgeActions;
  onDone?: () => void;
  patient?: PatientProfilePatient;
  patientId: PatientId;
}) {
  const { actions, onDone, patient, patientId } = props;
  const { api } = useApi();
  const queryClient = useQueryClient();
  const agencyMembersQuery = useAgencyMembers();
  const authorizationsQueryKey = React.useMemo(
    () => ["patientProfileModal", patientId, "authorizations"],
    [patientId]
  );
  const authorizationsQuery = useQuery({
    queryKey: authorizationsQueryKey,
    queryFn: () => api.get("./authorizations/:patientId", { path: { patientId } }),
  });
  const authorizations = authorizationsQuery.data?.authorizations ?? [];
  const agencyMemberNamesById = React.useMemo(
    () =>
      new Map(
        (agencyMembersQuery.data ?? []).map((agencyMember) => [
          agencyMember.id,
          formatAgencyMemberName(agencyMember),
        ])
      ),
    [agencyMembersQuery.data]
  );
  const refreshAuthorizations = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: authorizationsQueryKey });
    onDone?.();
  }, [authorizationsQueryKey, onDone, queryClient]);
  const handleOpenAuthorization = React.useCallback(
    (authorization: PatientAuthorizationRow) => {
      if (patient === undefined) {
        return;
      }

      actions.openAuthorization({
        patient,
        data: authorization,
        onDone: refreshAuthorizations,
      });
    },
    [actions, patient, refreshAuthorizations]
  );
  const handleUploadAuthorizationFile = React.useCallback(
    (authorization: PatientAuthorizationRow) => {
      if (patient === undefined) {
        return;
      }

      actions.uploadAuthorizationFile({
        patient,
        data: authorization,
        onDone: refreshAuthorizations,
      });
    },
    [actions, patient, refreshAuthorizations]
  );
  const handleOpenAuthorizationFile = React.useCallback(
    (authorization: PatientAuthorizationRow, fileId: PatientAuthorizationRow["files"][number]) => {
      if (patient === undefined) {
        return;
      }

      actions.openAuthorizationFile({
        patient,
        data: { authorization, fileId },
      });
    },
    [actions, patient]
  );
  const handleOpenAuthorizationUtilization = React.useCallback(
    (authorization: PatientAuthorizationRow) => {
      if (patient === undefined) {
        return;
      }

      actions.openAuthorizationUtilization({
        patient,
        data: authorization,
      });
    },
    [actions, patient]
  );
  const handleDeleteAuthorization = React.useCallback(
    (authorization: PatientAuthorizationRow) => {
      if (patient === undefined) {
        return;
      }

      actions.deleteAuthorization({
        patient,
        data: authorization,
        onDone: refreshAuthorizations,
      });
    },
    [actions, patient, refreshAuthorizations]
  );
  const handleAutoAllocateAuthorization = React.useCallback(
    (authorization: PatientAuthorizationRow, full: boolean) => {
      if (patient === undefined) {
        return;
      }

      actions.autoAllocateAuthorization({
        patient,
        data: { authorization, full },
        onDone: refreshAuthorizations,
      });
    },
    [actions, patient, refreshAuthorizations]
  );
  const canRunAuthorizationAllocation =
    auth.isAllowedTo("patient_page_run_authorization_allocation") && patient !== undefined;
  const canRunFullAuthorizationAllocation =
    auth.isAllowedTo("patient_page_run_authorization_allocation_full") && patient !== undefined;
  const canDeleteAuthorization =
    auth.isAllowedTo("patient_page_delete_authorization") && patient !== undefined;
  const canEditAuthorization =
    auth.isAllowedTo("edit_patient_page_authorizations") && patient !== undefined;
  const authorizationColumns = React.useMemo<
    Array<EntityProfileDataTableColumn<PatientAuthorizationRow, AuthorizationSortKey>>
  >(
    () => [
      {
        key: "id",
        header: "ID",
        render: (authorization) => authorization.id,
        cellProps: { textAlign: "center" },
      },
      {
        key: "code",
        header: "Code",
        render: (authorization) => authorization.code,
        cellProps: { textAlign: "center" },
      },
      {
        key: "contract",
        header: "Contract",
        render: (authorization) =>
          getAuthorizationContractName(patient?.contracts ?? [], authorization),
        cellProps: { textAlign: "center" },
      },
      {
        key: "serviceCode",
        header: (
          <>
            Service
            <br />
            Code
          </>
        ),
        render: (authorization) =>
          authorization.serviceCodeDisplayName ??
          formatAuthorizationServiceCode(authorization.serviceCode),
        cellProps: { textAlign: "center" },
      },
      {
        key: "visitCreateOffice",
        header: (
          <>
            Visit Create
            <br />
            Office
          </>
        ),
        render: (authorization) => authorization.createVisitOfficeName ?? "",
        cellProps: { textAlign: "center" },
      },
      {
        key: "period",
        header: "Period",
        render: (authorization) => <AuthorizationPeriodValue authorization={authorization} />,
        cellProps: { minW: "230px", textAlign: "center" },
      },
      {
        key: "startDate",
        header: (
          <>
            Start
            <br />
            date
          </>
        ),
        render: (authorization) => formatAuthorizationDate(authorization.startDate),
        sortKey: "startDate",
        sortValue: (authorization) => String(authorization.startDate ?? ""),
        cellProps: { textAlign: "center", whiteSpace: "nowrap" },
      },
      {
        key: "endDate",
        header: (
          <>
            End
            <br />
            date
          </>
        ),
        render: (authorization) => formatAuthorizationDate(authorization.endDate),
        sortKey: "endDate",
        sortValue: (authorization) => String(authorization.endDate ?? ""),
        cellProps: { textAlign: "center", whiteSpace: "nowrap" },
      },
      {
        key: "remainingMaxHours",
        header: (
          <>
            Remaining / Max
            <br />
            Hours
          </>
        ),
        render: (authorization) =>
          `${formatAuthorizationHours(authorization.remainingMinutes)} / ${formatAuthorizationHours(
            authorization.maxMinutes
          )}`,
        cellProps: { textAlign: "center" },
      },
      {
        key: "notes",
        header: "Notes",
        render: (authorization) => (
          <>
            {authorization.note?.predefinedAnswerText ? (
              <Text>
                <Text as="span" fontWeight={700}>
                  Predefined answer:
                </Text>{" "}
                {authorization.note.predefinedAnswerText}
              </Text>
            ) : null}
            {authorization.note === null ? "" : authorization.note.noteRichText ?? ""}
          </>
        ),
        cellProps: { maxW: "260px", whiteSpace: "normal" },
      },
      {
        key: "files",
        header: "Files",
        render: (authorization) => (
          <VStack alignItems="stretch" spacing={1}>
            {(authorization.fileDetails ?? []).length > 0
              ? (authorization.fileDetails ?? []).map((file, fileIndex) => (
                  <EntityModalButton
                    key={file.id}
                    kind="blue"
                    size="xs"
                    style={{ textAlign: "left" }}
                    variant="link"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenAuthorizationFile(authorization, file.id);
                    }}
                  >
                    {file.fileName ?? `File ${fileIndex + 1}`}
                  </EntityModalButton>
                ))
              : (authorization.files ?? []).map((fileId, fileIndex) => (
                  <EntityModalButton
                    key={fileId}
                    kind="blue"
                    size="xs"
                    style={{ textAlign: "left" }}
                    variant="link"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenAuthorizationFile(authorization, fileId);
                    }}
                  >
                    File {fileIndex + 1}
                  </EntityModalButton>
                ))}
            <EntityModalButton
              kind="blue"
              size="xs"
              variant="outline"
              onClick={(event) => {
                event.stopPropagation();
                handleUploadAuthorizationFile(authorization);
              }}
            >
              Upload file
            </EntityModalButton>
          </VStack>
        ),
      },
      {
        key: "created",
        header: "Created",
        render: (authorization) => (
          <AuthorizationAuditValue
            date={authorization.createdAt}
            name={
              getAuthorizationAgencyMemberName(
                authorization,
                "createdByName",
                "createdBy",
                agencyMemberNamesById
              ) ?? ""
            }
          />
        ),
        cellProps: { textAlign: "center" },
      },
      {
        key: "updated",
        header: "Updated",
        render: (authorization) => (
          <AuthorizationAuditValue
            date={authorization.updatedAt}
            name={
              getAuthorizationAgencyMemberName(
                authorization,
                "updatedByName",
                "updatedBy",
                agencyMemberNamesById
              ) ?? ""
            }
          />
        ),
        cellProps: { textAlign: "center" },
      },
      {
        key: "action",
        header: "Action",
        render: (authorization) => (
          <Menu placement="bottom-end">
            <MenuButton
              _active={{ bg: "#fff" }}
              _hover={{ bg: "#fff" }}
              as={Button}
              bg="#fff"
              borderColor="#00bcd4"
              borderRadius="full"
              color="#00a6bd"
              fontSize="18px"
              h="30px"
              minW="30px"
              p={0}
              variant="outline"
              onClick={(event) => event.stopPropagation()}
            >
              ⋮
            </MenuButton>
            <MenuList fontSize="13px" minW="190px" onClick={(event) => event.stopPropagation()}>
              {canRunFullAuthorizationAllocation ? (
                <MenuItem onClick={() => handleAutoAllocateAuthorization(authorization, true)}>
                  Auto Allocate (Full)
                </MenuItem>
              ) : null}
              {canRunAuthorizationAllocation ? (
                <MenuItem onClick={() => handleAutoAllocateAuthorization(authorization, false)}>
                  Auto Allocate
                </MenuItem>
              ) : null}
              <MenuItem onClick={() => handleOpenAuthorizationUtilization(authorization)}>
                View
              </MenuItem>
              {!canDeleteAuthorization ? null : (
                <MenuItem color="red.600" onClick={() => handleDeleteAuthorization(authorization)}>
                  Delete Authorization
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        ),
        cellProps: { textAlign: "center" },
      },
    ],
    [
      agencyMemberNamesById,
      canDeleteAuthorization,
      canRunAuthorizationAllocation,
      canRunFullAuthorizationAllocation,
      handleAutoAllocateAuthorization,
      handleDeleteAuthorization,
      handleOpenAuthorizationFile,
      handleOpenAuthorizationUtilization,
      handleUploadAuthorizationFile,
      patient,
    ]
  );

  return (
    <Box className="authorization-table" maxW="100%" mb={4} overflowX="auto" py={2}>
      <Text color="#001f3f" fontSize="24px" fontWeight={400} mb={2}>
        Authorizations
      </Text>
      <EntityProfileDataTable
        columns={authorizationColumns}
        defaultCellAlign="center"
        defaultHeaderAlign="center"
        defaultPageSize={3}
        defaultSort={{ key: "startDate", direction: "desc" }}
        emptyLabel={
          authorizationsQuery.isLoading
            ? "Loading authorizations..."
            : authorizationsQuery.isError
            ? "Unable to load authorizations"
            : "No authorizations"
        }
        getRowKey={(authorization, index) => authorization.id ?? index}
        minW="960px"
        pageSizeOptions={authorizationPageSizeOptions}
        rowProps={(authorization) => ({
          cursor: authorization.canEdit === false || !canEditAuthorization ? "default" : "pointer",
          _hover: { bg: "#f5f5f5" },
          onClick: () => {
            if (authorization.canEdit === false || !canEditAuthorization) {
              return;
            }

            handleOpenAuthorization(authorization);
          },
        })}
        rows={authorizations}
        tableSx={{
          borderCollapse: "collapse",
          "tbody td": {
            fontSize: "14px",
            lineHeight: "1.25",
            padding: "10px 6px",
          },
        }}
      />
    </Box>
  );
}

function PatientCalendarFilters(props: {
  showDeletedVisits: boolean;
  showAuthorizationDetails: boolean;
  showAuthorizationDetailsOption: boolean;
  showSummaryColumn: boolean;
  onChangeShowDeletedVisits: (value: boolean) => void;
  onChangeShowAuthorizationDetails: (value: boolean) => void;
  onChangeShowSummaryColumn: (value: boolean) => void;
}) {
  const checkboxStyles = {
    colorScheme: "blue" as const,
    size: "md" as const,
    w: "fit-content" as const,
  };
  const labelStyles = {
    color: "#2D2D2D",
    fontSize: "14px",
    fontWeight: 400,
  };

  return (
    <Stack alignItems="flex-start" mb={3} spacing={2}>
      <Checkbox
        {...checkboxStyles}
        isChecked={props.showDeletedVisits}
        onChange={(event) => props.onChangeShowDeletedVisits(event.target.checked)}
      >
        <Text {...labelStyles}>Show Deleted Visits</Text>
      </Checkbox>
      {props.showAuthorizationDetailsOption ? (
        <Checkbox
          {...checkboxStyles}
          isChecked={props.showAuthorizationDetails}
          onChange={(event) => props.onChangeShowAuthorizationDetails(event.target.checked)}
        >
          <Text {...labelStyles}>Show Authorization Details</Text>
        </Checkbox>
      ) : null}
      <Checkbox
        {...checkboxStyles}
        isChecked={props.showSummaryColumn}
        onChange={(event) => props.onChangeShowSummaryColumn(event.target.checked)}
      >
        <Text {...labelStyles}>Show Summary Column</Text>
      </Checkbox>
    </Stack>
  );
}

function PatientVacationDeleteDialog(props: {
  isLoading: boolean;
  isOpen: boolean;
  vacations: EntityProfileCalendarVacation[];
  onCancel: () => void;
  onConfirm: (regenerateVisits: boolean) => void;
}) {
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [selectedOption, setSelectedOption] = React.useState<"keep" | "regenerate">("keep");

  React.useEffect(() => {
    if (props.isOpen) {
      setSelectedOption("keep");
    }
  }, [props.isOpen]);

  return (
    <AlertDialog
      isCentered
      isOpen={props.isOpen}
      leastDestructiveRef={cancelRef}
      onClose={props.onCancel}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>Delete vacations</AlertDialogHeader>
          <AlertDialogBody>
            <VStack alignItems="stretch" spacing={4}>
              <Text whiteSpace="pre-wrap">Are you sure you want to delete selected vacations?</Text>
              <VStack alignItems="stretch" spacing={1}>
                {props.vacations.map((vacation, index) => (
                  <Text key={vacation.id} fontSize="14px">
                    {index + 1}. {formatEntityCalendarDate(vacation.date)}
                  </Text>
                ))}
              </VStack>
              <RadioGroup
                value={selectedOption}
                onChange={(value) => setSelectedOption(value as "keep" | "regenerate")}
              >
                <VStack alignItems="stretch" spacing={2}>
                  <Radio value="keep">Delete selected vacations</Radio>
                  <Radio value="regenerate">
                    Delete selected vacations and restore weekly template visits (if exist)
                  </Radio>
                </VStack>
              </RadioGroup>
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <EntityModalButton
              ref={cancelRef}
              disabled={props.isLoading}
              kind="white"
              onClick={props.onCancel}
            >
              Cancel
            </EntityModalButton>
            <EntityModalButton
              isLoading={props.isLoading}
              kind="red"
              style={{ marginLeft: 12 }}
              onClick={() => props.onConfirm(selectedOption === "regenerate")}
            >
              Delete
            </EntityModalButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

function PatientCalendarConfirmDialog(props: {
  confirmLabel?: string;
  isLoading: boolean;
  isOpen: boolean;
  message: React.ReactNode;
  title: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  return (
    <AlertDialog
      isCentered
      isOpen={props.isOpen}
      leastDestructiveRef={cancelRef}
      onClose={props.onCancel}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>{props.title}</AlertDialogHeader>
          <AlertDialogBody>{props.message}</AlertDialogBody>
          <AlertDialogFooter>
            <EntityModalButton
              ref={cancelRef}
              disabled={props.isLoading}
              kind="white"
              onClick={props.onCancel}
            >
              Cancel
            </EntityModalButton>
            <EntityModalButton
              isLoading={props.isLoading}
              kind="red"
              style={{ marginLeft: 12 }}
              onClick={props.onConfirm}
            >
              {props.confirmLabel ?? "Confirm"}
            </EntityModalButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

function PatientStopBroadcastDialog(props: {
  isLoading: boolean;
  isOpen: boolean;
  visits: EntityProfileCalendarVisit[];
  onCancel: () => void;
  onConfirm: (type: "visits" | "broadcasts") => void;
}) {
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const visitBroadcastIds = React.useMemo(
    () => [
      ...new Set(
        props.visits
          .map((visit) => visit.visitBroadcast?.visitBroadcastId)
          .filter(
            (visitBroadcastId): visitBroadcastId is VisitBroadcastId =>
              visitBroadcastId !== null && visitBroadcastId !== undefined
          )
      ),
    ],
    [props.visits]
  );
  const canStopSeries = visitBroadcastIds.length > 0 && props.visits.length > 1;
  const [selectedType, setSelectedType] = React.useState<"visits" | "broadcasts">("visits");

  React.useEffect(() => {
    if (props.isOpen) {
      setSelectedType("visits");
    }
  }, [props.isOpen]);

  return (
    <AlertDialog
      isCentered
      isOpen={props.isOpen}
      leastDestructiveRef={cancelRef}
      onClose={props.onCancel}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>Stop broadcasts</AlertDialogHeader>
          <AlertDialogBody>
            <VStack alignItems="stretch" spacing={4}>
              <Text>
                Stop broadcast for {props.visits.length} selected visit
                {props.visits.length === 1 ? "" : "s"}?
              </Text>
              {canStopSeries ? (
                <RadioGroup
                  value={selectedType}
                  onChange={(value) => setSelectedType(value as "visits" | "broadcasts")}
                >
                  <VStack alignItems="stretch" spacing={2}>
                    <Radio value="visits">Stop only selected visits</Radio>
                    <Radio value="broadcasts">
                      Stop entire broadcast series ({visitBroadcastIds.length})
                    </Radio>
                  </VStack>
                </RadioGroup>
              ) : null}
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <EntityModalButton
              ref={cancelRef}
              disabled={props.isLoading}
              kind="white"
              onClick={props.onCancel}
            >
              Cancel
            </EntityModalButton>
            <EntityModalButton
              isLoading={props.isLoading}
              kind="red"
              style={{ marginLeft: 12 }}
              onClick={() => props.onConfirm(canStopSeries ? selectedType : "visits")}
            >
              Stop broadcast
            </EntityModalButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

export default function PatientProfileCalendarSection(props: Props) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [monthDate, setMonthDate] = React.useState(() => LocalDate.now());
  const [listDateRange, setListDateRange] = React.useState<[LocalDate, LocalDate | null]>(() => {
    const today = LocalDate.now();

    return [today.withDayOfMonth(1), today.plusMonths(1).withDayOfMonth(1).minusDays(1)];
  });
  const handleListDateRangeChange = React.useCallback((range: [LocalDate, LocalDate | null]) => {
    setListDateRange(range);
    setMonthDate(range[0]);
  }, []);
  const [showDeletedVisits, setShowDeletedVisits] = useLocalStorage(
    patientCalendarStorageKeys.showDeletedVisits,
    true
  );
  const [showAuthorizationDetails, setShowAuthorizationDetails] = useLocalStorage(
    patientCalendarStorageKeys.showAuthorizationDetails,
    false
  );
  const [showSummaryColumn, setShowSummaryColumn] = useLocalStorage(
    patientCalendarStorageKeys.showSummaryColumn,
    true
  );
  const canShowAuthorizationDetails =
    props.showBillingFeature && auth.isAllowedTo("view_patient_page_authorizations");
  const [calendarSelection, setCalendarSelection] = React.useState<PatientCalendarSelection>({
    type: "CURRENT_SHIFTS",
  });
  const [selectionSeeds, setSelectionSeeds] = React.useState<EntityProfileCalendarVisit[]>([]);
  const [isSelectingVisits, setIsSelectingVisits] = React.useState(false);
  const [selectionError, setSelectionError] = React.useState<string | null>(null);
  const selectionRequest = React.useRef(0);
  const [selectedTaskMetricsInstanceId, setSelectedTaskMetricsInstanceId] =
    React.useState<PatientTaskInstanceId | null>(null);
  const [broadcastVisitsToStop, setBroadcastVisitsToStop] = React.useState<
    EntityProfileCalendarVisit[] | null
  >(null);
  const [flexibleBroadcastToStop, setFlexibleBroadcastToStop] =
    React.useState<VisitBroadcastId | null>(null);
  const [taskBroadcastToStop, setTaskBroadcastToStop] =
    React.useState<PatientTaskInstanceId | null>(null);
  const hasSignaledCalendarReadyRef = React.useRef(false);

  const visibleDates = React.useMemo(
    () => getEntityCalendarVisibleMonthDates(monthDate),
    [monthDate]
  );
  const listRangeEnd = listDateRange[1] ?? listDateRange[0];
  const calendarQuery = usePatientProfileCalendarRange(
    props.patientId,
    props.view === "list" ? listDateRange[0] : visibleDates.queryFrom,
    props.view === "list" ? listRangeEnd : visibleDates.queryTo,
    props.isHidden !== true
  );
  const certificationsQuery = useQuery({
    queryKey: ["patientProfileModal", "calendar", "certificationsSettings"],
    queryFn: () => api.get("./certifications_settings", {}),
  });
  const hasLoadedCalendarItems = calendarQuery.isFetched;

  React.useEffect(() => {
    if (!hasLoadedCalendarItems || hasSignaledCalendarReadyRef.current) {
      return;
    }

    const medflytWindow = window as Window & { __medflytEntityProfileCalendarReady?: boolean };

    hasSignaledCalendarReadyRef.current = true;
    medflytWindow.__medflytEntityProfileCalendarReady = true;
    medflytWindow.dispatchEvent(new Event(entityProfileCalendarReadyEvent));
  }, [hasLoadedCalendarItems]);

  const response = calendarQuery.data;
  const allVisits = React.useMemo(
    () => (response?.visitInstances ?? []).map(normalizeVisit),
    [response?.visitInstances]
  );
  const visits = React.useMemo(
    () => (showDeletedVisits ? allVisits : allVisits.filter((visit) => visit.status !== "deleted")),
    [allVisits, showDeletedVisits]
  );
  const listVisits = React.useMemo(
    () =>
      allVisits.filter(
        (visit) =>
          visit.status !== "deleted" &&
          isDateInRange(toEntityCalendarLocalDate(visit.date), listDateRange[0], listRangeEnd)
      ),
    [allVisits, listDateRange, listRangeEnd]
  );
  const vacations = React.useMemo(
    () => (response?.patientVacations ?? []).map(normalizeVacation),
    [response?.patientVacations]
  );
  const notes = React.useMemo(() => (response?.notes ?? []).map(normalizeNote), [response?.notes]);
  const patientTaskInstances = React.useMemo(
    () => response?.tasks.patientTaskInstances ?? [],
    [response?.tasks.patientTaskInstances]
  );
  const selectionRange = {
    from: props.view === "list" ? listDateRange[0] : visibleDates.queryFrom,
    to: props.view === "list" ? listRangeEnd : visibleDates.queryTo,
    isFetched: calendarQuery.isSuccess && !calendarQuery.isPlaceholderData,
  };
  const visitSelection = useCalendarSelection(visits, selectionRange);
  const vacationSelection = useCalendarSelection<EntityProfileCalendarVacation>(
    vacations,
    selectionRange
  );
  const {
    ids: selectedVisitIds,
    setIds: setSelectedVisitIds,
    selected: selectedVisits,
  } = visitSelection;
  const {
    ids: selectedVacationIds,
    setIds: setSelectedVacationIds,
    selected: selectedVacations,
  } = vacationSelection;
  const selectedListVisits = selectedVisits.filter((visit) => visit.status !== "deleted");
  const certificationOptions = React.useMemo(
    () => [
      ...new Set(
        (certificationsQuery.data?.certifications ?? [])
          .filter((certification) => certification.isActive)
          .map((certification) => certification.certification)
          .filter((certification): certification is string => certification != null)
      ),
    ],
    [certificationsQuery.data?.certifications]
  );
  const isLoading = calendarQuery.isLoading;
  const isError = calendarQuery.isError;
  // Refetch by query key rather than through the query objects: `useQuery`
  // returns a new result object on every render, so depending on those here
  // would make this callback — and everything memoized from it, down to
  // `markers` — change identity on every render.
  //
  // One change usually calls this several times in the same tick (the
  // `medflyt:refresh_visits` listener of the calendar and of the list view, and
  // the action's `onDone`). `cancelRefetch: false` joins the request already in
  // flight instead of cancelling it and sending another.
  const refetchCalendarQueries = React.useCallback(() => {
    void queryClient.refetchQueries(
      {
        type: "active",
        queryKey: getPatientCalendarQueryKeyPrefix(props.patientId),
      },
      { cancelRefetch: false }
    );
    void queryClient.invalidateQueries({ queryKey: ["patient", "get", props.patientId] });
  }, [props.patientId, queryClient]);
  React.useEffect(() => {
    const handleRefreshVisits = () => {
      selectionRequest.current += 1;
      setCalendarSelection({ type: "CURRENT_SHIFTS" });
      setSelectionSeeds([]);
      setIsSelectingVisits(false);
      setSelectedVisitIds([]);
      setSelectedVacationIds([]);
      refetchCalendarQueries();
    };

    window.addEventListener("medflyt:refresh_visits", handleRefreshVisits);

    return () => window.removeEventListener("medflyt:refresh_visits", handleRefreshVisits);
  }, [refetchCalendarQueries, setSelectedVisitIds, setSelectedVacationIds]);
  const deleteVacationsMutation = useMutation({
    mutationFn: (params: { vacationIds: PatientVacationId[]; regenerateVisits: boolean }) =>
      api.post("./patients/:patientId/patient_vacations/delete", {
        body: {
          ids: params.vacationIds,
          regenerateVisits: params.regenerateVisits,
        },
        path: {
          patientId: props.patientId,
        },
      }),
    onSuccess: () => {
      setSelectedVacationIds([]);
      refetchCalendarQueries();
      toast({
        description: "Selected vacations successfully deleted",
        position: "top-right",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Can't delete selected vacations",
        position: "top-right",
        status: "error",
      });
    },
  });
  const stopVisitBroadcastsMutation = useMutation({
    mutationFn: (params: { type: "visits" | "broadcasts"; visits: EntityProfileCalendarVisit[] }) =>
      api.put(
        "/agencies/:agencyId/agency_member/:agencyMemberId/patients/:patientId/broadcast_visits/stop",
        {
          body:
            params.type === "broadcasts"
              ? {
                  type: params.type,
                  visitBroadcastIds: [
                    ...new Set(
                      params.visits
                        .map((visit) => visit.visitBroadcast?.visitBroadcastId)
                        .filter(
                          (visitBroadcastId): visitBroadcastId is VisitBroadcastId =>
                            visitBroadcastId !== null && visitBroadcastId !== undefined
                        )
                    ),
                  ],
                }
              : {
                  type: params.type,
                  visitInstanceIds: params.visits.map((visit) => visit.id),
                },
          path: {
            patientId: props.patientId,
          },
        }
      ),
    onSuccess: () => {
      setBroadcastVisitsToStop(null);
      setSelectedVisitIds([]);
      refetchCalendarQueries();
      toast({
        description: "Selected broadcasts successfully stopped",
        position: "top-right",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Can't stop selected broadcasts",
        position: "top-right",
        status: "error",
      });
    },
  });
  const stopFlexibleBroadcastMutation = useMutation({
    mutationFn: (visitBroadcastId: VisitBroadcastId) =>
      api.delete(
        "/agencies/:agencyId/agency_member/:agencyMemberId/broadcast_visits/:visitBroadcastId",
        {
          path: {
            visitBroadcastId,
          },
        }
      ),
    onSuccess: () => {
      setFlexibleBroadcastToStop(null);
      refetchCalendarQueries();
      toast({
        description: "Flexible visit broadcast successfully stopped",
        position: "top-right",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Can't stop flexible visit broadcast",
        position: "top-right",
        status: "error",
      });
    },
  });
  const stopTaskBroadcastMutation = useMutation({
    mutationFn: (taskInstanceId: PatientTaskInstanceId) =>
      api.delete("./patient_task_instances_broadcast/:taskInstanceId", {
        path: {
          taskInstanceId,
        },
      }),
    onSuccess: () => {
      setTaskBroadcastToStop(null);
      refetchCalendarQueries();
      toast({
        description: "Successfully stopped task broadcast",
        position: "top-right",
        status: "success",
      });
    },
    onError: (error: unknown) => {
      const errorStatus =
        typeof error === "object" && error !== null && "status" in error
          ? (error as { status?: number }).status
          : undefined;

      toast({
        description:
          errorStatus === 409
            ? "A caregiver has already taken the task. Refresh the calendar to see the latest status."
            : "Can't stop task broadcast",
        position: "top-right",
        status: "error",
      });
    },
  });
  const handleStopVisitBroadcasts = React.useCallback(
    (visitsToStop: EntityProfileCalendarVisit[]) => {
      if (visitsToStop.length === 0) {
        return;
      }

      setBroadcastVisitsToStop(visitsToStop);
    },
    []
  );
  const handleStopFlexibleBroadcast = React.useCallback((visitBroadcastId: VisitBroadcastId) => {
    setFlexibleBroadcastToStop(visitBroadcastId);
  }, []);
  const handleEditTask = React.useCallback(
    (task: PatientCalendarTaskLike) => {
      if (props.patient !== undefined) {
        props.actions.openTask({ patient: props.patient, task, onDone: refetchCalendarQueries });
      }
    },
    [props.actions, props.patient, refetchCalendarQueries]
  );
  const handleCancelTask = React.useCallback(
    (task: PatientCalendarTaskLike) => {
      if (props.patient !== undefined) {
        props.actions.cancelTask({ patient: props.patient, task, onDone: refetchCalendarQueries });
      }
    },
    [props.actions, props.patient, refetchCalendarQueries]
  );
  const handleScheduleTask = React.useCallback(
    (task: PatientCalendarTaskLike) => {
      if (props.patient !== undefined) {
        props.actions.scheduleTask({
          patient: props.patient,
          task,
          onDone: refetchCalendarQueries,
        });
      }
    },
    [props.actions, props.patient, refetchCalendarQueries]
  );
  const handleStopTaskBroadcast = React.useCallback(
    (task: PatientCalendarTaskLike) => {
      if (task.taskInstanceId === undefined) {
        toast({
          description: "Can't stop task broadcast without a task instance.",
          position: "top-right",
          status: "error",
        });
        return;
      }

      setTaskBroadcastToStop(task.taskInstanceId);
    },
    [toast]
  );
  const handleOpenTaskMetrics = React.useCallback(
    (task: PatientCalendarTaskLike) => {
      if (task.taskInstanceId === undefined) {
        toast({
          description: "Can't open task metrics without a task instance.",
          position: "top-right",
          status: "error",
        });
        return;
      }

      setSelectedTaskMetricsInstanceId(task.taskInstanceId);
    },
    [toast]
  );
  const taskMarkers = React.useMemo(
    () =>
      patientTaskInstances
        .map((task) =>
          normalizeTask(task, {
            onCancelTask: handleCancelTask,
            onEditTask: handleEditTask,
            onOpenTaskMetrics: handleOpenTaskMetrics,
            onScheduleTask: handleScheduleTask,
            onStopTaskBroadcast: handleStopTaskBroadcast,
          })
        )
        .filter((marker): marker is EntityProfileCalendarMarker => marker !== null),
    [
      handleCancelTask,
      handleEditTask,
      handleOpenTaskMetrics,
      handleScheduleTask,
      handleStopTaskBroadcast,
      patientTaskInstances,
    ]
  );
  const broadcastMarkers = React.useMemo(
    () =>
      (response?.flexibleVisitBroadcasts ?? []).flatMap((broadcast) =>
        normalizeFlexibleBroadcast(broadcast, props.actions, handleStopFlexibleBroadcast)
      ),
    [response?.flexibleVisitBroadcasts, handleStopFlexibleBroadcast, props.actions]
  );
  const markers = React.useMemo(
    () => [...taskMarkers, ...broadcastMarkers],
    [broadcastMarkers, taskMarkers]
  );

  const filters =
    props.view === "calendar" ? (
      <PatientCalendarFilters
        showAuthorizationDetails={showAuthorizationDetails}
        showAuthorizationDetailsOption={canShowAuthorizationDetails}
        showDeletedVisits={showDeletedVisits}
        showSummaryColumn={showSummaryColumn}
        onChangeShowAuthorizationDetails={setShowAuthorizationDetails}
        onChangeShowDeletedVisits={setShowDeletedVisits}
        onChangeShowSummaryColumn={setShowSummaryColumn}
      />
    ) : null;
  const resetRecurringSelection = () => {
    selectionRequest.current += 1;
    setIsSelectingVisits(false);
    setSelectionError(null);
    setCalendarSelection({ type: "CURRENT_SHIFTS" });
    setSelectionSeeds([]);
  };
  const handleToggleVisitSelection = (visit: EntityProfileCalendarVisit) => {
    resetRecurringSelection();
    visitSelection.toggle(visit);
  };
  const handleRecurringSelectionChange = async (selection: PatientCalendarSelection) => {
    const seeds = selectionSeeds.length > 0 ? selectionSeeds : selectedVisits;
    const requestId = ++selectionRequest.current;
    setCalendarSelection(selection);
    setSelectionSeeds(seeds);
    setSelectionError(null);
    if (selection.type === "CURRENT_SHIFTS") {
      visitSelection.replace(seeds);
      setIsSelectingVisits(false);
      return;
    }
    if (props.patient === undefined || (selection.type === "UNTIL_DATE" && !selection.untilDate)) {
      setIsSelectingVisits(false);
      return;
    }
    setIsSelectingVisits(true);
    try {
      const expanded = await props.actions.selectCalendarVisits({
        patient: props.patient,
        selectedVisits: seeds,
        selection,
      });
      if (requestId === selectionRequest.current) visitSelection.replace(expanded);
    } catch {
      if (requestId === selectionRequest.current)
        setSelectionError("Unable to select these shifts. Please try again.");
    } finally {
      if (requestId === selectionRequest.current) setIsSelectingVisits(false);
    }
  };

  const handleToggleVacationSelection = vacationSelection.toggle;
  const handleListVisibleSelectionChange = React.useCallback(
    (selection: { vacationIds: PatientVacationId[]; visitIds: VisitInstanceId[] }) => {
      const visibleVisitIds = new Set(selection.visitIds);
      const visibleVacationIds = new Set(selection.vacationIds);

      setSelectedVisitIds((current) =>
        filterPreservingIdentity(current, (selectedVisitId) => visibleVisitIds.has(selectedVisitId))
      );
      setSelectedVacationIds((current) =>
        filterPreservingIdentity(current, (selectedVacationId) =>
          visibleVacationIds.has(selectedVacationId)
        )
      );
    },
    [setSelectedVisitIds, setSelectedVacationIds]
  );
  const handleConfirmStopVisitBroadcasts = (type: "visits" | "broadcasts") => {
    if (broadcastVisitsToStop === null) {
      return;
    }

    stopVisitBroadcastsMutation.mutate({
      type,
      visits: broadcastVisitsToStop,
    });
  };
  const handleConfirmStopFlexibleBroadcast = () => {
    if (flexibleBroadcastToStop === null) {
      return;
    }

    stopFlexibleBroadcastMutation.mutate(flexibleBroadcastToStop);
  };
  const handleConfirmStopTaskBroadcast = () => {
    if (taskBroadcastToStop === null) {
      return;
    }

    stopTaskBroadcastMutation.mutate(taskBroadcastToStop);
  };
  const [vacationsToDelete, setVacationsToDelete] = React.useState<
    EntityProfileCalendarVacation[] | null
  >(null);
  const handleDeleteVacationsByIds = React.useCallback(
    (vacationIds: PatientVacationId[]) => {
      if (vacationIds.length === 0) {
        return;
      }

      const vacationsById = new Map<PatientVacationId, EntityProfileCalendarVacation>(
        vacations.map((vacation) => [vacation.id, vacation])
      );
      const targets = vacationIds
        .map((vacationId) => vacationsById.get(vacationId))
        .filter((vacation): vacation is EntityProfileCalendarVacation => vacation !== undefined);

      if (targets.length === 0) {
        return;
      }

      setVacationsToDelete(targets);
    },
    [vacations]
  );
  const handleDeleteVacations = React.useCallback(() => {
    if (selectedVacationIds.length === 0) {
      return;
    }

    handleDeleteVacationsByIds(selectedVacationIds);
  }, [handleDeleteVacationsByIds, selectedVacationIds]);
  const handleConfirmDeleteVacations = (regenerateVisits: boolean) => {
    if (vacationsToDelete === null) {
      return;
    }

    deleteVacationsMutation.mutate(
      {
        vacationIds: vacationsToDelete.map((vacation) => vacation.id),
        regenerateVisits,
      },
      {
        onSuccess: () => setVacationsToDelete(null),
      }
    );
  };
  const dayMenuItems = React.useCallback(
    (context: {
      date: LocalDate;
      vacations: EntityProfileCalendarVacation[];
      visits: EntityProfileCalendarVisit[];
    }) => {
      const hasVacation = context.vacations.length > 0;
      const items: Array<{ label: string; onClick: () => void }> = [];

      if (props.patient === undefined) {
        return items;
      }
      const patient = props.patient;

      if (
        hasVacation === false &&
        auth.isAllowedTo("create_new_patient_visit" as Messages["AgencyMemberPermission"])
      ) {
        items.push({
          label: "New Visit",
          onClick: () =>
            props.actions.openNewVisit({
              calendarDate: context.date.toString(),
              patient,
            }),
        });
      }

      if (
        hasVacation === false &&
        auth.isAllowedTo("edit_new_patient_task" as Messages["AgencyMemberPermission"])
      ) {
        items.push({
          label: "New Task",
          onClick: () =>
            props.actions.openNewTask({
              calendarDate: context.date.toString(),
              patient,
            }),
        });
      }

      if (auth.isAllowedTo("edit_new_patient_note" as Messages["AgencyMemberPermission"])) {
        items.push({
          label: "New Note",
          onClick: () =>
            props.actions.openNewCalendarNote({
              calendarDate: context.date.toString(),
              patient,
              onDone: refetchCalendarQueries,
            }),
        });
      }

      if (auth.isAllowedTo("edit_new_patient_vacation" as Messages["AgencyMemberPermission"])) {
        items.push(
          hasVacation
            ? {
                label: "Delete Vacation",
                onClick: () =>
                  handleDeleteVacationsByIds(context.vacations.map((vacation) => vacation.id)),
              }
            : {
                label: "Set On Vacation",
                onClick: () =>
                  props.actions.openNewVacation({
                    calendarDate: context.date.toString(),
                    patient,
                    onDone: refetchCalendarQueries,
                  }),
              }
        );
      }

      return items;
    },
    [handleDeleteVacationsByIds, props.actions, props.patient, refetchCalendarQueries]
  );

  const seeds = selectionSeeds.length > 0 ? selectionSeeds : selectedVisits;
  const seedWeekdays = [
    ...new Set(seeds.map((visit) => toEntityCalendarLocalDate(visit.date).dayOfWeek().value())),
  ].sort();
  const weekdayNames = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const weekdaysLabel = seedWeekdays.map((day) => `${weekdayNames[day - 1]}s`).join(", ");
  const canSelectRecurring =
    seeds.length > 0 &&
    selectedVacations.length === 0 &&
    seeds.every((visit) => visit.status !== "deleted" && visit.status !== "task");
  const isPermanentSelection = seeds.every((visit) => visit.visitBatchType === "PERMANENT");
  const selectionDisabledReason = "This option is available only on permanent visits";
  const isSelectionReady =
    !isSelectingVisits &&
    !selectionError &&
    (calendarSelection.type !== "UNTIL_DATE" || calendarSelection.untilDate !== undefined);
  const selectionControls =
    selectedVisits.length > 0 || selectedVacations.length > 0 || selectionSeeds.length > 0 ? (
      <VStack alignItems="stretch" border="1px solid #ddd" borderRadius="4px" p={4} spacing={3}>
        <Text>
          {selectedVisits.length} {selectedVisits.length === 1 ? "shift" : "shifts"} selected
          {selectedVacations.length > 0 ? `, ${selectedVacations.length} vacations selected` : ""}.
        </Text>
        {canSelectRecurring ? (
          <>
            <RadioGroup
              value={calendarSelection.type}
              onChange={(type) =>
                void handleRecurringSelectionChange({
                  type: type as PatientCalendarSelection["type"],
                  untilDate: calendarSelection.untilDate,
                })
              }
            >
              <Stack direction={{ base: "column", xl: "row" }} spacing={6}>
                {(
                  [
                    {
                      type: "CURRENT_SHIFTS",
                      text: `Choose selected shifts (${seeds.length})`,
                      tooltip: "Will not impact weekly template",
                    },
                    {
                      type: "CHOSEN_DAYS",
                      text: `Choose all ${weekdaysLabel} going forward`,
                      tooltip: "Will impact weekly template (except temporary visits)",
                    },
                    {
                      type: "UNTIL_DATE",
                      text: `Choose all ${weekdaysLabel} until date`,
                      tooltip: "Will not impact weekly template",
                    },
                  ] as const
                ).map((option) => {
                  const isDisabled = option.type !== "CURRENT_SHIFTS" && !isPermanentSelection;
                  return (
                    <Tooltip
                      key={option.type}
                      hasArrow
                      shouldWrapChildren
                      isDisabled={!isDisabled}
                      label={selectionDisabledReason}
                    >
                      <Radio isDisabled={isDisabled} value={option.type}>
                        <Tooltip hasArrow label={option.tooltip} placement="top">
                          <Box as="span">
                            {option.text} <IosInformationCircleIcon aria-hidden boxSize="14px" />
                          </Box>
                        </Tooltip>
                      </Radio>
                    </Tooltip>
                  );
                })}
              </Stack>
            </RadioGroup>
            <Box maxW="240px">
              <Text fontSize="sm" mb={1}>
                Choose end date
              </Text>
              <SingleDatePicker
                disabled={calendarSelection.type !== "UNTIL_DATE" || !isPermanentSelection}
                maxDate={
                  seeds.every((visit) => visit.visitBatchMaxEndDate != null)
                    ? seeds
                        .map((visit) => toEntityCalendarLocalDate(visit.visitBatchMaxEndDate!))
                        .reduce((last, date) => (date.isAfter(last) ? date : last))
                        .atStartOfDay()
                    : undefined
                }
                minDate={seeds
                  .reduce((first, visit) => {
                    const date = toEntityCalendarLocalDate(visit.date);
                    return date.isAfter(first) ? date : first;
                  }, toEntityCalendarLocalDate(seeds[0].date))
                  .atStartOfDay()}
                placeholderText="Choose end date"
                selected={
                  calendarSelection.untilDate ? LocalDate.parse(calendarSelection.untilDate) : null
                }
                onChange={(date) =>
                  void handleRecurringSelectionChange({
                    type: "UNTIL_DATE",
                    untilDate: date?.toString(),
                  })
                }
              />
            </Box>
          </>
        ) : null}
        {isSelectingVisits ? <Text role="status">Loading selected shifts...</Text> : null}
        {selectionError ? (
          <Text color="red.600" role="alert">
            {selectionError}
          </Text>
        ) : null}
        <EntityModalButton
          kind="white"
          size="sm"
          onClick={() => {
            resetRecurringSelection();
            visitSelection.replace([]);
            vacationSelection.replace([]);
          }}
        >
          Reset selection
        </EntityModalButton>
      </VStack>
    ) : null;

  return (
    <Box>
      {filters}
      {props.view === "calendar" && canShowAuthorizationDetails && showAuthorizationDetails ? (
        <PatientAuthorizationDetailsTable
          actions={props.actions}
          patient={props.patient}
          patientId={props.patientId}
          onDone={refetchCalendarQueries}
        />
      ) : null}
      <EntityProfileCalendar
        actionButtons={
          props.patient === undefined ? null : (
            <PatientCalendarActionButtons
              actions={props.actions}
              calendarSelection={calendarSelection}
              isSelectingVisits={!isSelectionReady}
              isStoppingBroadcasts={stopVisitBroadcastsMutation.isPending || !isSelectionReady}
              patient={props.patient}
              selectedVacations={selectedVacations}
              selectedVisits={selectedVisits}
              onActionDone={refetchCalendarQueries}
              onDeleteVacations={handleDeleteVacations}
              onStopBroadcasts={handleStopVisitBroadcasts}
            />
          )
        }
        dayMenuItems={dayMenuItems}
        errorLabel="Unable to load patient calendar."
        isError={isError}
        isLoading={isLoading}
        listActionButtons={
          props.patient === undefined ? null : (
            <PatientCalendarListActionButtons
              actions={props.actions}
              calendarSelection={calendarSelection}
              isSelectingVisits={!isSelectionReady}
              isStoppingBroadcasts={stopVisitBroadcastsMutation.isPending || !isSelectionReady}
              patient={props.patient}
              selectedVacations={selectedVacations}
              selectedVisits={selectedListVisits}
              onActionDone={refetchCalendarQueries}
              onDeleteVacations={handleDeleteVacations}
              onStopBroadcasts={handleStopVisitBroadcasts}
            />
          )
        }
        listCertificationOptions={certificationOptions}
        listDateRange={listDateRange}
        markers={markers}
        monthDate={monthDate}
        notes={notes}
        selectedVacationIds={selectedVacationIds}
        selectedVisitIds={selectedVisitIds}
        selectionControls={selectionControls}
        showAuthorizationDetails={canShowAuthorizationDetails && showAuthorizationDetails}
        showSummaryColumn={showSummaryColumn}
        vacations={vacations}
        view={props.view}
        visibleDates={visibleDates.dates}
        visits={props.view === "list" ? listVisits : visits}
        onListDateRangeChange={handleListDateRangeChange}
        onListVisibleSelectionChange={handleListVisibleSelectionChange}
        onMonthDateChange={setMonthDate}
        onRetry={() => {
          void calendarQuery.refetch();
        }}
        onToggleVacationSelection={handleToggleVacationSelection}
        onToggleVisitSelection={handleToggleVisitSelection}
        onVisitClick={(visit) => {
          const visitBroadcastId = visit.visitBroadcast?.visitBroadcastId;

          if (
            visit.broadcastKind !== null &&
            visit.broadcastKind !== undefined &&
            visitBroadcastId !== null &&
            visitBroadcastId !== undefined
          ) {
            props.actions.navigateToVisitBroadcast(visitBroadcastId);
            return;
          }

          props.actions.navigateToVisitInstance(visit.id);
        }}
        onVisitNameClick={(visit) => {
          if (visit.caregiverId != null)
            props.actions.navigateToCaregiver(CaregiverId.parse(visit.caregiverId));
          else props.actions.navigateToVisitInstance(visit.id);
        }}
      />
      {selectedTaskMetricsInstanceId !== null ? (
        <PatientTaskMetricsModal
          patientTaskInstanceId={selectedTaskMetricsInstanceId}
          onClose={() => setSelectedTaskMetricsInstanceId(null)}
        />
      ) : null}
      <PatientVacationDeleteDialog
        isLoading={deleteVacationsMutation.isPending}
        isOpen={vacationsToDelete !== null}
        vacations={vacationsToDelete ?? []}
        onCancel={() => setVacationsToDelete(null)}
        onConfirm={handleConfirmDeleteVacations}
      />
      <PatientStopBroadcastDialog
        isLoading={stopVisitBroadcastsMutation.isPending}
        isOpen={broadcastVisitsToStop !== null}
        visits={broadcastVisitsToStop ?? []}
        onCancel={() => setBroadcastVisitsToStop(null)}
        onConfirm={handleConfirmStopVisitBroadcasts}
      />
      <PatientCalendarConfirmDialog
        confirmLabel="Stop broadcast"
        isLoading={stopFlexibleBroadcastMutation.isPending}
        isOpen={flexibleBroadcastToStop !== null}
        message="Stop flexible visit broadcast?"
        title="Stop broadcast"
        onCancel={() => setFlexibleBroadcastToStop(null)}
        onConfirm={handleConfirmStopFlexibleBroadcast}
      />
      <PatientCalendarConfirmDialog
        confirmLabel="Stop broadcast"
        isLoading={stopTaskBroadcastMutation.isPending}
        isOpen={taskBroadcastToStop !== null}
        message="Stop selected task broadcast?"
        title="Stop task broadcast"
        onCancel={() => setTaskBroadcastToStop(null)}
        onConfirm={handleConfirmStopTaskBroadcast}
      />
    </Box>
  );
}
